import { useId } from 'react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/format'
import { useTweenedArray } from '@/hooks/use-tweened-array'
import {
    areaPath,
    smoothPath,
    svgId,
    toPoints
} from '@/lib/chart'

export interface ChartSeries {
    label: string
    values: Array<number>
    tone?: 'primary' | 'muted'
    area?: boolean
    dashed?: boolean
}

interface ProjectionChartProps {
    series: Array<ChartSeries>
    max: number
    target?: number
    targetLabel?: string
    xLabels: [string, string, string]
    className?: string
}

const WIDTH = 640
const HEIGHT = 260
const PAD_TOP = 14
const PAD_BOTTOM = 8

/** Smooth projection chart whose lines glide to new values as inputs change. */
export const ProjectionChart = ({ series, max, target, targetLabel, xLabels, className }: ProjectionChartProps) => {
    const gradientId = `projection-${svgId(useId())}`
    const lengths = series.map(item => item.values.length)
    const flat = [...series.flatMap(item => item.values), max, target ?? 0]
    const tweened = useTweenedArray(flat)

    let cursor = 0

    const animated = series.map((item, index) => {
        const values = tweened.slice(cursor, cursor + lengths[index])

        cursor += lengths[index]

        return { ...item, values }
    })

    const domainMax = Math.max(1, tweened[tweened.length - 2])
    const targetValue = tweened[tweened.length - 1]
    const scale = { width: WIDTH, height: HEIGHT, padTop: PAD_TOP, padBottom: PAD_BOTTOM, min: 0, max: domainMax }
    const yOf = (value: number) => PAD_TOP + (1 - value / domainMax) * (HEIGHT - PAD_TOP - PAD_BOTTOM)

    return (
        <figure className={cn('relative', className)}>
            <div className='relative aspect-[640/260] w-full'>
                <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className='absolute inset-0 h-full w-full overflow-visible' role='img' aria-label={series.map(item => item.label).join(' and ')}>
                    <defs>
                        <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='0%' stopColor='#e2f37a' stopOpacity='0.32' />
                            <stop offset='100%' stopColor='#e2f37a' stopOpacity='0' />
                        </linearGradient>
                    </defs>

                    {[0, 0.25, 0.5, 0.75, 1].map(share => (
                        <line key={share} x1='0' x2={WIDTH} y1={yOf(domainMax * share)} y2={yOf(domainMax * share)} stroke='rgba(255,255,255,0.06)' />
                    ))}

                    {target !== undefined && targetValue > 0 && targetValue <= domainMax && (
                        <line x1='0' x2={WIDTH} y1={yOf(targetValue)} y2={yOf(targetValue)} stroke='rgba(243,239,230,0.45)' strokeDasharray='4 6' />
                    )}

                    {animated.map(item => {
                        const points = toPoints(item.values, scale)
                        const primary = item.tone !== 'muted'

                        return (
                            <g key={item.label}>
                                {item.area && <path d={areaPath(points, HEIGHT - PAD_BOTTOM)} fill={`url(#${gradientId})`} />}

                                <path
                                    d={smoothPath(points)}
                                    fill='none'
                                    stroke={primary ? '#e2f37a' : 'rgba(243,239,230,0.45)'}
                                    strokeWidth={primary ? 2.4 : 1.8}
                                    strokeDasharray={item.dashed ? '5 6' : undefined}
                                    strokeLinecap='round'
                                />
                            </g>
                        )
                    })}
                </svg>

                {[1, 0.5].map(share => (
                    <span
                        key={share}
                        className='pointer-events-none absolute left-0 -translate-y-full pb-1 text-[10px] tabular-nums text-cream/35'
                        style={{ top: `${(yOf(domainMax * share) / HEIGHT) * 100}%` }}
                    >
                        {formatCurrency(domainMax * share, { compact: true })}
                    </span>
                ))}

                {target !== undefined && targetLabel && targetValue > 0 && targetValue <= domainMax && (
                    <span
                        className='pointer-events-none absolute right-0 -translate-y-full pb-1 text-[10px] text-cream/60'
                        style={{ top: `${(yOf(targetValue) / HEIGHT) * 100}%` }}
                    >
                        {targetLabel}
                    </span>
                )}
            </div>

            <figcaption className='mt-3 flex justify-between text-[11px] text-cream/40'>
                {xLabels.map(label => (
                    <span key={label}>
                        {label}
                    </span>
                ))}
            </figcaption>

            {series.length > 1 && (
                <div className='mt-3 flex flex-wrap gap-4 text-[11px] text-cream/55'>
                    {series.map(item => (
                        <span key={item.label} className='flex items-center gap-1.5'>
                            <span className={cn('h-0.5 w-4 rounded-full', item.tone === 'muted' ? 'bg-cream/45' : 'bg-citron')} />
                            {item.label}
                        </span>
                    ))}
                </div>
            )}
        </figure>
    )
}
